import { api, type Overview as Data } from '../api';
import { Banner, Card, Empty, Kpi, Loading, useAsync } from '../ui';

/**
 * Overview.
 *
 * The first thing an operator sees each morning. Ordered by what they act on:
 * anything wrong at the top, then the float (which gates everything), then
 * volume.
 */
export function Overview({ go }: { go: (route: string) => void }) {
  const { data, error, loading } = useAsync<Data>(() => api.overview(), []);

  if (loading) return <Loading label="Loading overview" />;
  if (error) return <Banner tone="danger" title="Could not load the overview">{error}</Banner>;
  if (!data) return null;

  const { kpis, float, queue, webhooks, trend } = data;
  const runway = float?.estimated_runway?.payouts ?? null;
  const lowFloat =
    float && BigInt(float.available) <= BigInt(float.low_balance_threshold) && BigInt(float.low_balance_threshold) > 0n;

  const peak = Math.max(1, ...trend.map((t) => t.received));

  return (
    <div className="stack">
      {float?.frozen && (
        <Banner tone="danger" title="Float frozen" action={<button className="btn sm" onClick={() => go('float')}>Open float</button>}>
          {float.frozen_reason ?? 'Reconciliation mismatch'}. No payouts will be initiated until this is cleared.
        </Banner>
      )}
      {lowFloat && !float?.frozen && (
        <Banner tone="warn" title="Float is low" action={<button className="btn sm" onClick={() => go('float')}>Top up</button>}>
          {float!.available_display} available — at or below the alert threshold.
          {runway !== null && ` About ${runway} more payouts at the current average.`}
        </Banner>
      )}
      {kpis.flagged > 0 && (
        <Banner tone="warn" title={`${kpis.flagged} referral${kpis.flagged === 1 ? '' : 's'} awaiting review`}
          action={<button className="btn sm" onClick={() => go('flagged')}>Open queue</button>}>
          These are held out of the payout path until a reviewer decides.
        </Banner>
      )}
      {queue.dead > 0 && (
        <Banner tone="danger" title="Dead-lettered jobs" action={<button className="btn sm" onClick={() => go('ops')}>Investigate</button>}>
          {queue.dead} job{queue.dead === 1 ? '' : 's'} exhausted their retries and are not being processed.
        </Banner>
      )}

      <div className="grid cols-4">
        <Kpi label="Referrals processed" value={kpis.processed.toLocaleString()} foot={`${kpis.pending_kyc} awaiting identity`} />
        <Kpi label="Paid" value={kpis.paid.toLocaleString()} foot={<><strong>{kpis.paid_amount_display}</strong> disbursed</>} />
        <Kpi label="Awaiting payout" value={kpis.awaiting_payout.toLocaleString()} foot={`${kpis.flagged} flagged for review`} />
        <Kpi label="Rejected or expired" value={kpis.rejected.toLocaleString()} foot="Never reached the payout stage" />
      </div>

      <div className="grid cols-2">
        <Card title="Float" action={<button className="btn sm ghost" onClick={() => go('float')}>Ledger →</button>}>
          {float ? (
            <>
              <div className="row" style={{ alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
                <div className="value figure" style={{ fontFamily: 'var(--font-display)', fontSize: 27, fontWeight: 600 }}>
                  {float.available_display}
                </div>
                <div className="small muted">available</div>
              </div>
              <FloatMeter float={float} />
              <dl className="dl" style={{ marginTop: 16 }}>
                <dt>Balance</dt><dd>{float.balance_display}</dd>
                <dt>Active holds</dt><dd>{float.active_holds_display}</dd>
                <dt>Est. runway</dt>
                <dd>{runway === null ? <span className="muted">not enough history</span> : `${runway} payouts`}</dd>
              </dl>
            </>
          ) : (
            <Empty title="No float account" hint="Configure a programme float before payouts can run." />
          )}
        </Card>

        <Card title="Last 14 days">
          {trend.length === 0 ? (
            <Empty title="No activity yet" />
          ) : (
            <>
              <div className="trend" role="img" aria-label="Referrals received and paid per day over the last 14 days">
                {trend.map((t) => {
                  const h = (t.received / peak) * 62;
                  const paidH = t.received === 0 ? 0 : (t.paid / t.received) * h;
                  return (
                    <div className="bar" key={t.day} title={`${t.day}: ${t.received} received, ${t.paid} paid`}>
                      <div className="seg rest" style={{ height: Math.max(0, h - paidH) }} />
                      <div className="seg paid" style={{ height: paidH }} />
                    </div>
                  );
                })}
              </div>
              <div className="trend-axis">
                {trend.map((t, i) => (
                  <span key={t.day}>{i % 2 === 0 ? new Date(t.day).getDate() : ''}</span>
                ))}
              </div>
              <div className="meter-key" style={{ marginTop: 12 }}>
                <span><i style={{ background: 'var(--accent)' }} />Paid</span>
                <span><i style={{ background: 'var(--rule-strong)' }} />Received, not yet paid</span>
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="Work queue">
          <dl className="dl">
            <dt>Pending</dt><dd>{queue.pending.toLocaleString()}</dd>
            <dt>Running</dt><dd>{queue.running.toLocaleString()}</dd>
            <dt>Dead-lettered</dt>
            <dd style={queue.dead > 0 ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>{queue.dead.toLocaleString()}</dd>
          </dl>
        </Card>
        <Card title="Webhooks to inDrive">
          <dl className="dl">
            <dt>Delivered</dt><dd>{webhooks.delivered.toLocaleString()}</dd>
            <dt>Pending</dt><dd>{webhooks.pending.toLocaleString()}</dd>
            <dt>Dead-lettered</dt>
            <dd style={webhooks.dead > 0 ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>{webhooks.dead.toLocaleString()}</dd>
          </dl>
          {webhooks.pending > 0 && webhooks.delivered === 0 && webhooks.dead === 0 && (
            <p className="small muted" style={{ margin: '14px 0 0', lineHeight: 1.6 }}>
              Events are queued but none have been delivered — inDrive has most likely not registered a
              callback endpoint yet. Nothing is lost: the state lives in the database and the backlog
              delivers as soon as an endpoint is registered.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

export function FloatMeter({ float }: { float: { balance: string; available: string; active_holds: string } }) {
  const balance = Number(BigInt(float.balance));
  const available = Number(BigInt(float.available));
  const held = Number(BigInt(float.active_holds));
  const total = Math.max(1, balance);

  return (
    <>
      <div className="meter">
        <div className="seg-avail" style={{ width: `${(available / total) * 100}%` }} />
        <div className="seg-held" style={{ width: `${(held / total) * 100}%` }} />
      </div>
      <div className="meter-key">
        <span><i style={{ background: 'var(--accent)' }} />Available</span>
        <span><i style={{ background: 'var(--warn)' }} />Committed to payouts in flight</span>
      </div>
    </>
  );
}
